import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QrCode from '../components/QrCode';

/**
 * アプリ本体のオリジン（2026-08-21 のホスト分割用）。
 *
 * 分割後、LP は `kinlog.jp` に残りアプリは `app.kinlog.jp` へ移るため、
 * ログイン・登録への導線だけが**別オリジンへの遷移**になる。
 * ここが相対パスのままだと `kinlog.jp/login` へ飛び、**301 を1往復余計に挟む**
 * （2026-08-25: この 301 は恒久で残すことにしたので 404 にはならないが、
 *  往復の分だけ遅くなる。設定して直接 app を指すのが正しい）。
 *
 * **未設定なら従来どおり相対パスで SPA 内遷移する**。つまり
 * この値を入れるまで挙動は1ミリも変わらない。切替は env を足すだけ。
 * `VITE_API_URL` / `VITE_TURNSTILE_SITE_KEY` と同じくビルド時に焼き込む。
 */
const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string | undefined)?.replace(/\/$/, '') || '';

const CTA_LABEL = '14日間、無料ではじめる';
// 問い合わせはフォームへ。mailto: はメールソフト未設定の端末（特にスマホ・共有PC）で
// 何も起きず、問い合わせ経路が事実上消えるため使わない。
const CONTACT_PATH = '/contact';

// 人数別の月額早見（10名まで一律¥1,000、11名目以降 +¥100/名・税抜）
const priceFor = (people: number): number =>
  people <= 10 ? 1000 : 1000 + (people - 10) * 100;

const PRICE_ROWS = [5, 10, 20, 50];

/**
 * 「はじめるまで」の手順。**実装の事実だけを書くこと。**
 * - 企業登録で役職・休暇区分・社員番号の既定が作られる(server/src/routes/companies.ts)
 * - 従業員登録の必須項目は username / password / name の3つ(server/src/routes/users.ts)
 * 実装が変わったらここも直す。対外文書に無い機能を書かない。
 */
/**
 * 共用端末の説明。**見本のQRであって実在のトークンではない**(値は固定のダミー)。
 * 64桁の16進数にしているのは、本物と同じ型番のQRを描かせて見た目を実物に合わせるため。
 */
const SAMPLE_QR = 'a3f97c21b8e40d65f1a2c9038de74b1502a6cf83917be40d25ca6f381b04e79c';

const KIOSK_POINTS: string[] = [
  '端末は管理画面から登録します。<strong>打刻しかできない画面</strong>なので、勤怠の閲覧や設定には入れません。',
  'QRコードは<strong>名刺サイズで印刷して配れます</strong>(A4用紙1枚に10名分)。スマートフォンを持つ人は自分の画面に表示することもできます。',
  '紛失しても<strong>その人のぶんだけ作り直せます</strong>。配り直した時点で、古いQRコードは使えなくなります。',
  '<strong>追加料金はかかりません。</strong>台数の制限もありません。',
];

/**
 * 画面のスクリーンショット。**デモ用テナントの架空データ**で撮ったもの。
 * 差し替えるときも実在の顧客データが写っていないことを必ず確かめること。
 */
const SCREENS: { src: string; alt: string; title: string; note: string; w: number; h: number }[] = [
  {
    src: '/lp/screen-dashboard.jpg',
    alt: 'キンログの打刻画面。現在時刻と出勤時刻が表示され、退勤打刻と休憩開始のボタンが並んでいる。',
    title: '打刻',
    note: 'ボタンを押すだけ。いま出勤中かどうかも同じ画面で分かります。',
    w: 754,
    h: 302,
  },
  {
    src: '/lp/screen-leave.jpg',
    alt: 'キンログの休暇残数の一覧。従業員ごとに有給の付与日数・消費日数・残数・有効期限が並んでいる。',
    title: '休暇の残数',
    note: '付与と取得から残日数を自動で計算します。聞かれる前に本人も見られます。',
    w: 855,
    h: 520,
  },
  {
    src: '/lp/screen-history.jpg',
    alt: 'キンログの勤怠履歴。日付ごとの出退勤時刻・休憩・労働時間が一覧で並んでいる。',
    title: '勤怠履歴',
    note: '日ごとの記録がそのまま残ります。あとから探す手間がありません。',
    w: 612,
    h: 405,
  },
];

const START_STEPS: { n: string; title: string; desc: string }[] = [
  {
    n: '1',
    title: '会社を登録する',
    desc: '会社名とメールアドレスを入れるだけ。クレジットカードの登録は要りません。この時点で休暇の区分も権限も作られています。',
  },
  {
    n: '2',
    title: '従業員を登録する',
    desc: '要るのは氏名・ログインID・パスワードの3つだけ。従業員一人ひとりのメールアドレスは要りません。パート・アルバイトの多い職場でも止まりません。',
  },
  {
    // 見出しの「やることは2つだけ」と数を合わせる。ここは作業ではなく結果なので番号を振らない
    n: '✓',
    title: 'あとは打刻するだけ',
    desc: 'ブラウザを開いてボタンを押すだけ。時刻はサーバー側で記録するので、端末の時計がずれていても勤怠は狂いません。',
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    // Reveal animation on scroll
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add('on'), i * 75);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.rv').forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, []);

  /**
   * アプリへの導線を `<a>` として描くための組み合わせ。
   *
   * **なぜボタンではなくリンクなのか**: トップページは静的HTMLとして
   * プリレンダーされる（`scripts/prerender-home.mjs`）。`<button onClick>` だと
   * **JS が読み込まれるまで押しても何も起きない**。本文が先に描かれる分だけ
   * 「表示されているのに反応しないCTA」の窓が生まれる（JS は 500KB 超）。
   * `href` を持つリンクなら JS 抜きで機能し、キーボード操作・新しいタブで開く・
   * クローラからの到達も同時に満たせる。
   *
   * `onClick` が要るのは APP_ORIGIN 未設定のときだけ。同一オリジンでは
   * 通常のリンク遷移だとページ全体を読み直すため、SPA 内遷移に倒す。
   */
  const appHref = (path: string) => `${APP_ORIGIN}${path}`;

  const onAppLinkClick = (path: string) => (e: React.MouseEvent) => {
    if (APP_ORIGIN) return; // 別オリジン: 通常のリンク遷移に任せる
    e.preventDefault();
    navigate(path);
  };

  const faqs = [
    {
      q: '導入するとき、最初にどんな設定が必要ですか？',
      a: '打刻を始めるために管理者が決めなければならない設定はありません。会社を登録した時点で、有給休暇などの休暇区分・全権限を持つ「管理者」の役職・社員番号の採番が入っています。曜日や祝日の扱いにも既定値が入っており、必要になったら後から変更できます。実際にやるのは従業員の登録だけです。',
    },
    {
      q: '従業員一人ひとりにメールアドレスは必要ですか？',
      a: '必要ありません。従業員の登録に要るのは氏名・ログインID・パスワードの3つだけです。パート・アルバイトが中心で、全員分のメールアドレスを用意できない職場でもそのまま始められます。',
    },
    {
      q: '全員がスマートフォンを持っていなくても使えますか？',
      a: '使えます。店舗や現場に置いたタブレット・パソコンを共用の打刻端末にして、従業員はQRコードをかざすだけで打刻できます。QRコードは名刺サイズで印刷して配れます（A4用紙1枚に10名分）。共用端末の台数に制限はなく、追加料金もかかりません。',
    },
    {
      q: '紙のタイムカードから切り替えるとき、過去の記録はどうなりますか？',
      a: 'これまでの紙のカードは、そのまま保管してください。労働関係の記録には保存期間の定めがあります。キンログには切り替えた日以降の打刻が入るため、過去分を入力し直す作業は必要ありません。詳しくは「紙のタイムカードをやめる手順」の記事をご覧ください。',
    },
    {
      q: '無料トライアルは本当に無料ですか？クレジットカードの登録は必要ですか？',
      a: '14日間の無料トライアルは費用が一切かかりません。トライアル開始時にクレジットカードの登録は不要です。期間中に解約された場合も料金は発生しません。',
    },
    {
      q: '利用人数が増減した場合、料金はどうなりますか？',
      a: '10名までは一律で月額1,000円（税抜）です。11名目以降は1名につき月額+100円（税抜）。最低利用人数の縛りはなく、人数の増減に合わせて柔軟にご利用いただけます。',
    },
    {
      q: 'スマートフォンから打刻できますか？',
      a: 'はい。スマートフォンのブラウザから出退勤・休憩の打刻が可能です。アプリストアからのインストールは不要です。さらに、スマートフォンやパソコンのホーム画面（デスクトップ）に追加しておけば、アイコンから1タップでアプリのように起動できます。',
    },
    {
      q: '勤怠データはCSVやPDFで出力できますか？',
      a: '月次の勤怠データをCSVおよびPDFで出力できます。給与計算ソフトへの取り込みや、書類としての提出にご利用いただけます。',
    },
    {
      q: '解約はいつでもできますか？違約金はかかりますか？',
      a: 'いつでも解約できます。最低利用期間の縛りや解約手数料・違約金は一切ありません。初期費用もかかりません。',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FFFFFF', fontFamily: "'Zen Kaku Gothic New', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Zen+Kaku+Gothic+New:wght@400;700;900&family=Outfit:wght@400;600;800&display=swap');

        html {
          scroll-behavior: smooth;
        }

        /* 実際の画面。2列 → 狭い画面では1列 */
        .shot-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 32px;
        }
        @media (max-width: 760px) {
          .shot-grid { grid-template-columns: 1fr; gap: 28px; }
        }

        /* 共用端末セクション。狭い画面では縦に積む */
        .kiosk-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
          align-items: center;
          gap: 56px;
        }
        @media (max-width: 900px) {
          .kiosk-grid { grid-template-columns: 1fr; gap: 40px; }
        }

        /* 「はじめるまで」。番号を線でつないで、順序が一目で読めるようにする */
        .start-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
        }
        .start-step { position: relative; }
        /* 番号どうしを結ぶ線。最後の1つには引かない */
        .start-step:not(:last-child)::after {
          content: '';
          position: absolute;
          top: 19px;
          left: 58px;
          right: -30px;
          height: 2px;
          background: #DBEAFE;
        }
        @media (max-width: 860px) {
          .start-grid { grid-template-columns: 1fr; }
          .start-step:not(:last-child)::after { display: none; }
        }

        /* セクション共通の横パディング（レスポンシブ） */
        .sec-pad {
          padding-left: clamp(20px, 5vw, 48px);
          padding-right: clamp(20px, 5vw, 48px);
        }

        .rv {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.65s, transform 0.65s;
        }

        .rv.on {
          opacity: 1;
          transform: translateY(0);
        }

        @keyframes up {
          from { opacity: 0; transform: translateY(22px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* 背景装飾は静止（浮遊／回転アニメは廃止） */
        .badge { animation: up 0.6s both; }
        h1 { animation: up 0.7s 0.1s both; }
        .hs { animation: up 0.7s 0.2s both; }
        .btns { animation: up 0.7s 0.3s both; }
        .sts { animation: up 0.7s 0.4s both; }
        .hero-mock { animation: up 0.8s 0.35s both; }

        /* 機能グリッド：3列→2列→1列 */
        .feat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
          position: relative;
        }

        /* ハンバーガー（モバイル時のみ表示） */
        .nav-toggle { display: none; }

        /* デスクトップ：リンク群を表示、ハンバーガーは非表示 */
        .nav-links { display: flex; }
        .nav-mobile-panel { display: none; }

        @media (max-width: 900px) {
          .feat-grid { grid-template-columns: repeat(2, 1fr); }
          /* ヒーロー：2カラム→縦積み（テキスト上→モック下） */
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 44px !important;
          }
          .hero-copy { max-width: 720px !important; }
          .hero-mock { max-width: 560px; margin: 0 auto; width: 100%; }
          /* 縦積み時はデバイスの傾きを弱めて見切れを防ぐ */
          .hero-device { transform: rotate(-2deg) !important; }
        }

        @media (max-width: 860px) {
          /* リンク群は隠し、ハンバーガー＋常時CTAを表示 */
          .nav-links { display: none; }
          .nav-toggle { display: inline-flex; }
          .nav-mobile-panel.open { display: flex; }
        }

        @media (max-width: 640px) {
          .feat-grid { grid-template-columns: 1fr; }
          .hero-stats { flex-wrap: wrap; gap: 24px !important; }
          .hero-stats > div { flex: 1 1 40%; }
          h1 { font-size: clamp(2rem, 8vw, 2.6rem) !important; }
          .hero-mock { max-width: 100%; }
          .hero-device { transform: rotate(0deg) !important; }
        }

        @media (max-width: 420px) {
          .hero-stats > div { flex: 1 1 100%; }
        }
      `}</style>

      {/* NAV */}
      <nav
        className="sec-pad"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '14px',
          paddingBottom: '14px',
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(37, 99, 235, 0.1)',
        }}
      >
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', flexShrink: 0 }}
        >
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.06em' }}>
            勤怠管理
          </span>
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              fontSize: '1.45rem',
              color: '#2563EB',
              letterSpacing: '-0.03em',
            }}
          >
            キン<b style={{ color: '#1A2E4A', fontWeight: 800 }}>ログ</b>
          </span>
        </a>

        {/* デスクトップ：リンク + ログイン + CTA */}
        <ul className="nav-links" style={{ listStyle: 'none', alignItems: 'center', gap: '28px', margin: 0, padding: 0 }}>
          <li>
            <a href="#start" style={navLinkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#2563EB')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#64748B')}>はじめかた</a>
          </li>
          <li>
            <a href="#feat" style={navLinkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#2563EB')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#64748B')}>機能</a>
          </li>
          <li>
            <a href="#pricing" style={navLinkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#2563EB')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#64748B')}>料金</a>
          </li>
          <li>
            <a href="#faq" style={navLinkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#2563EB')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#64748B')}>よくある質問</a>
          </li>
          <li>
            {/*
              記事一覧(/blog/)は SPA の外にある静的HTML。React Router に食わせると
              ルートが無く LP へ落ちるため、**通常のリンクとして遷移させる**
              (onClick で preventDefault しないこと)。フッターの導線も同様。
            */}
            <a href="/blog/" style={navLinkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#2563EB')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#64748B')}>記事一覧</a>
          </li>
          <li>
            <a
              href={appHref('/login')}
              onClick={onAppLinkClick('/login')}
              style={navOutlineBtnStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#2563EB'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#2563EB'; }}
            >
              ログイン
            </a>
          </li>
          <li>
            <a
              href={appHref('/company-register')}
              onClick={onAppLinkClick('/company-register')}
              style={navFillBtnStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4ED8')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#2563EB')}
            >
              {CTA_LABEL}
            </a>
          </li>
        </ul>

        {/* モバイル：常時CTA（小） + ハンバーガー */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <a
            className="nav-toggle"
            href={appHref('/company-register')}
            onClick={onAppLinkClick('/company-register')}
            style={{
              ...navFillBtnStyle,
              padding: '8px 16px',
              fontSize: '0.8rem',
            }}
          >
            無料ではじめる
          </a>
          <button
            className="nav-toggle"
            aria-label="メニューを開く"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              border: '2px solid rgba(37, 99, 235, 0.3)',
              background: 'transparent',
              cursor: 'pointer',
              color: '#2563EB',
              fontSize: '1.3rem',
              lineHeight: 1,
            }}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* モバイル展開パネル */}
        <div
          className={`nav-mobile-panel${menuOpen ? ' open' : ''}`}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            flexDirection: 'column',
            gap: '4px',
            padding: '12px clamp(20px, 5vw, 48px) 18px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(14px)',
            borderBottom: '1px solid rgba(37, 99, 235, 0.12)',
          }}
        >
          {[
            { label: 'はじめかた', href: '#start' },
            { label: '共用端末', href: '#kiosk' },
            { label: '機能', href: '#feat' },
            { label: '料金', href: '#pricing' },
            { label: 'よくある質問', href: '#faq' },
            // 記事一覧は SPA 外の静的HTML。ハッシュではなく実URLなので通常遷移になる
            { label: '記事一覧', href: '/blog/' },
          ].map((it) => (
            <a
              key={it.href}
              href={it.href}
              onClick={() => setMenuOpen(false)}
              style={{ ...navLinkStyle, padding: '12px 4px', fontSize: '1rem' }}
            >
              {it.label}
            </a>
          ))}
          <a
            href={appHref('/login')}
            onClick={(e) => { setMenuOpen(false); onAppLinkClick('/login')(e); }}
            style={{ ...navOutlineBtnStyle, marginTop: '8px', width: '100%', padding: '12px' }}
          >
            ログイン
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section
        id="hero"
        className="sec-pad"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: '120px',
          paddingBottom: '72px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 660px 560px at 68% 48%, rgba(245, 158, 11, 0.26), transparent 70%), radial-gradient(ellipse 380px 380px at 14% 78%, rgba(37, 99, 235, 0.11), transparent 60%)',
          }}
        />
        {/* 背景装飾（静止）: 有機的なブロブと破線リングは廃止。
            細いグリッドのみを敷き、中央へ放射マスクでフェードさせる。
            傾けた矩形アウトラインも試したが主張が強すぎたため外した。 */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(37, 99, 235, 0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(37, 99, 235, 0.055) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse 70% 70% at 70% 50%, #000 30%, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 70% 70% at 70% 50%, #000 30%, transparent 75%)',
            zIndex: 0,
          }}
        />
        <div
          className="hero-grid"
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            alignItems: 'center',
            gap: '48px',
          }}
        >
        <div className="hero-copy" style={{ maxWidth: '620px' }}>
          <div
            className="badge"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              background: '#FEF3C7',
              color: '#1D4ED8',
              fontSize: '0.8rem',
              fontWeight: 700,
              padding: '7px 16px',
              borderRadius: '8px',
              marginBottom: '28px',
            }}
          >
            登録したその日から打刻できます
          </div>
          <h1
            style={{
              fontSize: 'clamp(2.2rem, 4.8vw, 3.6rem)',
              fontWeight: 900,
              lineHeight: 1.22,
              letterSpacing: '-0.03em',
              marginBottom: '22px',
            }}
          >
            紙のタイムカードから、
            <br />
            <em
              style={{ fontStyle: 'normal', color: '#2563EB', position: 'relative' }}
            >
              その日のうちに
              <span
                style={{
                  position: 'absolute',
                  bottom: '2px',
                  left: 0,
                  right: 0,
                  height: '5px',
                  background: '#F59E0B',
                  borderRadius: '4px',
                  zIndex: -1,
                  opacity: 0.7,
                  display: 'block',
                }}
              />
            </em>
            。
          </h1>
          <p
            className="hs"
            style={{
              fontSize: '1.05rem',
              lineHeight: 1.85,
              color: '#64748B',
              marginBottom: '40px',
            }}
          >
            <strong style={{ color: '#1E293B' }}>決めなければ始められない設定は、ありません。</strong>
            <br />
            会社を登録した時点で、有給などの休暇の種類も権限も入っています。
            <br />
            あとは従業員を登録するだけで、その日から打刻できます。
          </p>
          <div className="btns" style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <a
              href={appHref('/company-register')}
              onClick={onAppLinkClick('/company-register')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                background: '#2563EB',
                color: '#fff',
                fontSize: '0.97rem',
                fontWeight: 700,
                padding: '15px 34px',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 8px 22px rgba(37, 99, 235, 0.33)',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1D4ED8';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(37, 99, 235, 0.43)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2563EB';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 22px rgba(37, 99, 235, 0.33)';
              }}
            >
              ✦ {CTA_LABEL}
            </a>
            <a
              href="#start"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                background: 'transparent',
                color: '#1E293B',
                fontSize: '0.97rem',
                fontWeight: 700,
                padding: '15px 30px',
                borderRadius: '8px',
                textDecoration: 'none',
                border: '2px solid rgba(30, 41, 59, 0.14)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2563EB';
                e.currentTarget.style.color = '#2563EB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(30, 41, 59, 0.14)';
                e.currentTarget.style.color = '#1E293B';
              }}
            >
              はじめかたを見る →
            </a>
          </div>
          <div className="sts hero-stats" style={{ display: 'flex', gap: '44px', marginTop: '54px' }}>
            <div>
              <strong style={heroStatNumStyle}>3つ</strong>
              <span style={heroStatSubStyle}>
                従業員の登録に要る項目
                <br />
                メールアドレスは不要
              </span>
            </div>
            <div>
              <strong style={heroStatNumStyle}>0円</strong>
              <span style={heroStatSubStyle}>
                初期費用ゼロ
                <br />
                最低利用人数なし
              </span>
            </div>
            <div>
              <strong style={heroStatNumStyle}>
                月額
                <br />
                1,000円〜
              </strong>
              <span style={heroStatSubStyle}>
                10名まで一律
                <br />
                以降1名+100円
              </span>
            </div>
          </div>
        </div>

        {/* 製品モック画面: OG画像と同様に少し傾けたデバイスフレーム＋シャドウで見せる */}
        <div className="hero-mock" style={{ position: 'relative' }}>
          <div
            className="hero-device"
            style={{
              transform: 'rotate(-4deg)',
              transformOrigin: 'center',
              background: '#FFFFFF',
              borderRadius: '10px',
              padding: '14px',
              boxShadow: '0 18px 52px rgba(26, 46, 74, 0.30)',
            }}
          >
            <img
              src="/_branding/mock-screen.png"
              alt="キンログのダッシュボード画面。打刻・チーム出勤状況などの勤怠管理機能を1画面で確認できる。"
              loading="eager"
              width={920}
              height={760}
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                borderRadius: '14px',
                border: '1.5px solid #E2E8F0',
              }}
            />
          </div>
        </div>
        </div>
      </section>


      {/*
        START — 「はじめるまで」。
        紙・Excel から動けない理由は費用ではなく **移行の手間と失敗する怖さ** なので、
        価格より先に「何をやらされるのか」を出す。ここに書いてある3つは
        実装の事実(server/src/routes/companies.ts の企業登録で既定が作られ、
        users.ts の従業員登録の必須項目は username/password/name の3つだけ)。
      */}
      <section
        id="start"
        className="sec-pad"
        style={{ paddingTop: '92px', paddingBottom: '92px', background: '#FFFFFF' }}
      >
        <div className="rv" style={{ marginBottom: '52px' }}>
          <span style={eyebrowStyle}>Getting started</span>
          <h2 style={sectionH2Style}>
            はじめるまでに、
            <br />
            やることは2つだけ。
          </h2>
          <p style={{ fontSize: '0.97rem', color: '#64748B', lineHeight: 1.8, maxWidth: '620px' }}>
            勤怠管理システムの導入でいちばん重くなるのは、使い始めるまでの設定です。
            キンログは<strong style={{ color: '#1E293B' }}>会社を登録した時点で使える状態が出来上がります</strong>。
            打刻を始めるために管理者が決めなければならない設定はありません。
          </p>
        </div>

        <div className="start-grid">
          {START_STEPS.map((st) => (
            <div
              key={st.n}
              className="rv start-step"
              style={{ paddingRight: '10px' }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: st.n === '✓' ? '#F59E0B' : '#2563EB',
                  color: '#fff',
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  marginBottom: '20px',
                  // 連結線より前に出す。線は円の中心の高さを通る
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {st.n}
              </div>
              <h3 style={{ fontSize: '1.14rem', fontWeight: 800, marginBottom: '10px', letterSpacing: '-0.01em' }}>
                {st.title}
              </h3>
              <p style={{ fontSize: '0.92rem', color: '#64748B', lineHeight: 1.9, margin: 0, maxWidth: '300px' }}>{st.desc}</p>
            </div>
          ))}
        </div>

        {/*
          「設定できない」ではなく「決めなくても始められる」と書くこと。
          曜日設定・祝日設定は実際に存在し、既定値が入っている。
          無い機能を無いと書くのは良いが、有る機能を無いと書くと実物と食い違う。
        */}
        <div
          className="rv"
          style={{
            marginTop: '34px',
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '12px',
            padding: '26px 28px',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.94rem', lineHeight: 1.9, color: '#1E3A8A' }}>
            <strong>会社を登録した時点で入っているもの</strong>
            <br />
            有給休暇・病欠・特別休暇・代休の休暇区分／全権限を持つ「管理者」の役職／社員番号の採番。
            <br />
            曜日や祝日の扱いも既定値が入っています。<strong>後から変えられますが、決めなくても始められます。</strong>
          </p>
        </div>
      </section>


      {/*
        KIOSK — 共用端末のQR打刻。
        紙のタイムカードを使っている職場は「壁の打刻機」を置き換えたいのであって、
        全員にスマホを持たせたいわけではない。ここが FEATURES の一項目では弱いので
        独立したセクションにしている。
        ⚠️ 右の QR は**説明用の見本**。実際のトークンではない。
      */}
      <section
        id="kiosk"
        className="sec-pad"
        style={{ paddingTop: '92px', paddingBottom: '92px', background: '#0F172A', color: '#fff' }}
      >
        <div className="kiosk-grid">
          <div className="rv">
            <span style={{ ...eyebrowStyle, color: '#93C5FD' }}>Shared terminal</span>
            <h2 style={{ ...sectionH2Style, color: '#fff' }}>
              スマホを持たない人も、
              <br />
              1台の端末で打刻できる。
            </h2>
            <p style={{ fontSize: '0.97rem', color: '#CBD5E1', lineHeight: 1.9, marginBottom: '26px' }}>
              店や現場に置いたタブレット・パソコンを1台。従業員はQRコードをかざすだけで打刻できます。
              私物のスマートフォンを使わせる必要も、一人ひとりに端末を用意する必要もありません。
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '14px' }}>
              {KIOSK_POINTS.map((t) => (
                <li key={t} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span
                    aria-hidden
                    style={{
                      flexShrink: 0,
                      marginTop: '6px',
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: '#F59E0B',
                    }}
                  />
                  <span
                    style={{ fontSize: '0.94rem', lineHeight: 1.8, color: '#E2E8F0' }}
                    dangerouslySetInnerHTML={{ __html: t }}
                  />
                </li>
              ))}
            </ul>
            <p style={{ fontSize: '0.86rem', color: '#94A3B8', lineHeight: 1.85, marginTop: '26px' }}>
              QRコードだけでは打刻できません。<strong style={{ color: '#CBD5E1' }}>会社が登録した端末でしか読み取れない</strong>作りにしてあります。
            </p>
          </div>

          {/*
            端末に出る画面と、かざす側のQRカードを並べる。
            タブレットの画像は**実機と同じ経路で撮ったもの**(dev のデモテナントで
            実際にQRを読ませて打刻した画面)。作った絵ではない。
          */}
          <div className="rv" style={{ position: 'relative', paddingBottom: '72px' }}>
            <div
              style={{
                background: '#1E293B',
                borderRadius: '18px',
                padding: '14px',
                boxShadow: '0 26px 70px rgba(0, 0, 0, 0.5)',
                border: '1px solid #334155',
              }}
            >
              <img
                src="/lp/kiosk-screen.jpg"
                alt="共用端末の画面。QRコードを読み取り、田中 美咲さんの出勤を14時44分に記録したことが表示されている。"
                loading="lazy"
                width={1080}
                height={855}
                style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '8px' }}
              />
            </div>
            {/*
              本人のスマホに出る画面。**これは撮影したものではなく、実画面
              (`MyPunchQrPage`)の文言と構成に合わせて描いた図**。
              あの画面はログインしないと出ないため撮れていない。
              ⚠️ MyPunchQrPage を変えたらここも合わせること。
            */}
            <div
              style={{
                position: 'absolute',
                right: '4%',
                bottom: '-14px',
                width: '148px',
                background: '#1E293B',
                borderRadius: '20px',
                padding: '7px',
                boxShadow: '0 20px 44px rgba(0, 0, 0, 0.6)',
                border: '1px solid #334155',
              }}
            >
              <div
                style={{
                  background: '#F1F5F9',
                  borderRadius: '14px',
                  padding: '14px 10px 12px',
                  textAlign: 'center',
                  color: '#1E293B',
                }}
              >
                <div style={{ fontSize: '0.6rem', fontWeight: 700, marginBottom: '8px' }}>打刻用QRコード</div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <QrCode value={SAMPLE_QR} size={94} title="スマートフォンに表示した打刻用QRコード" />
                </div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, marginTop: '8px' }}>田中 美咲</div>
                <div style={{ fontSize: '0.5rem', color: '#64748B', lineHeight: 1.6, marginTop: '5px' }}>
                  共用端末のカメラに
                  <br />
                  この画面をかざしてください
                </div>
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                left: '-6px',
                bottom: '0',
                transform: 'rotate(-5deg)',
                background: '#fff',
                borderRadius: '10px',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                boxShadow: '0 22px 50px rgba(0, 0, 0, 0.55)',
                maxWidth: '272px',
              }}
            >
              <div style={{ flexShrink: 0 }}>
                <QrCode value={SAMPLE_QR} size={92} title="打刻用QRコードの見本" />
              </div>
              <div style={{ minWidth: 0, color: '#1E293B' }}>
                <div style={{ fontSize: '0.64rem', color: '#64748B', marginBottom: '3px' }}>サンプル商事株式会社</div>
                <div style={{ fontSize: '0.98rem', fontWeight: 800, lineHeight: 1.25 }}>田中 美咲</div>
                <div style={{ fontSize: '0.68rem', color: '#64748B', marginTop: '3px' }}>社員番号 002</div>
                <div style={{ fontSize: '0.6rem', color: '#94A3B8', marginTop: '8px' }}>打刻用QRコード</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="feat"
        className="sec-pad"
        style={{
          background: '#F1F5F9',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: '92px',
          paddingBottom: '92px',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-40px',
            left: '-40px',
            width: '420px',
            height: '420px',
            rotate: '45deg',
            background:
              'linear-gradient(135deg, rgba(245, 158, 11, 0.16), transparent 62%)',
            borderLeft: '1px solid rgba(245, 158, 11, 0.32)',
            borderTop: '1px solid rgba(245, 158, 11, 0.32)',
          }}
        />
        <div className="rv" style={{ marginBottom: '58px', position: 'relative' }}>
          <span style={eyebrowStyle}>Features</span>
          <h2 style={sectionH2Style}>
            紙とExcelでやっていた作業が、
            <br />
            そのまま消えます。
          </h2>
          <p style={{ fontSize: '0.97rem', color: '#64748B', lineHeight: 1.8, maxWidth: '560px' }}>
            機能の多さではなく、月末に手が止まっていた作業が実際に無くなるかで作っています。
          </p>
        </div>
        <div className="feat-grid">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rv"
              style={{
                background: '#fff',
                borderRadius: '10px',
                borderTop: '2px solid #2563EB',
                padding: '34px 30px',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.3s, box-shadow 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 18px 44px rgba(30, 41, 59, 0.09)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '22px',
                  background: '#FEF3C7',
                }}
              >
                {f.icon}
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px' }}>{f.title}</div>
              <p style={{ fontSize: '0.86rem', lineHeight: 1.75, color: '#64748B' }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>


      {/*
        SCREENS — 実際の画面。
        機能名を並べるより、**動いている画面を見せる方が「本当に使えるのか」に答える**。
        画像はデモ用テナントの架空データ(サンプル商事株式会社)で、実在の顧客データは含まない。
      */}
      <section
        className="sec-pad"
        style={{ paddingTop: '92px', paddingBottom: '92px', background: '#FFFFFF' }}
      >
        <div className="rv" style={{ marginBottom: '44px' }}>
          <span style={eyebrowStyle}>Screens</span>
          <h2 style={sectionH2Style}>使う画面は、これだけです。</h2>
          <p style={{ fontSize: '0.97rem', color: '#64748B', lineHeight: 1.8, maxWidth: '560px' }}>
            パソコンでもスマートフォンでも、同じ画面をブラウザで開くだけ。アプリのインストールは要りません。
          </p>
        </div>

        {/*
          画面は縮めて並べない。**縮めた瞬間に何も読めなくなり、雰囲気だけの絵になる。**
          撮ったままの大きさで1枚ずつ置き、幅の違いは中央寄せで吸収する。
        */}
        <div style={{ display: 'grid', gap: '28px' }}>
          {SCREENS.map((sc) => (
            <figure
              key={sc.src}
              className="rv"
              style={{
                margin: 0,
                background: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '14px',
                padding: 'clamp(20px, 3vw, 30px)',
              }}
            >
              <figcaption style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
                <span style={{ fontSize: '1.08rem', fontWeight: 800, letterSpacing: '-0.01em' }}>{sc.title}</span>
                <span style={{ fontSize: '0.9rem', color: '#64748B', lineHeight: 1.7 }}>{sc.note}</span>
              </figcaption>
              <div style={{ overflowX: 'auto' }}>
                <img
                  src={sc.src}
                  alt={sc.alt}
                  loading="lazy"
                  width={sc.w}
                  height={sc.h}
                  style={{
                    display: 'block',
                    margin: '0 auto',
                    // 撮ったより大きく描かない。拡大するとぼやける
                    width: '100%',
                    maxWidth: `${sc.w}px`,
                    height: 'auto',
                    borderRadius: '6px',
                    border: '1px solid #E2E8F0',
                  }}
                />
              </div>
            </figure>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="sec-pad" style={{ background: '#FFFFFF', paddingTop: '92px', paddingBottom: '92px', textAlign: 'center' }}>
        <div className="rv" style={{ marginBottom: '40px' }}>
          <span style={eyebrowStyle}>Pricing</span>
          <h2 style={{ ...sectionH2Style, textAlign: 'center' }}>
            料金は、これだけ。
            <br />
            わかりやすい一本。
          </h2>
          <p style={{ fontSize: '0.97rem', color: '#64748B', lineHeight: 1.8, maxWidth: '520px', margin: '0 auto' }}>
            初期費用0円・最低利用人数なし。14日間の無料トライアルはクレジットカード登録不要です。
          </p>
        </div>

        {/* 自社事実バッジ */}
        <div
          className="rv"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '40px',
          }}
        >
          {['初期費用 0円', '最低利用人数 なし', '月1,000円〜', 'カード登録不要', '解約手数料 0円'].map((b) => (
            <span key={b} style={factBadgeStyle}>
              ✓ {b}
            </span>
          ))}
        </div>

        <div
          className="rv"
          style={{
            maxWidth: '560px',
            margin: '0 auto',
            background: '#fff',
            borderRadius: '10px',
            padding: 'clamp(32px, 5vw, 48px) clamp(24px, 4vw, 44px)',
            border: '2px solid #2563EB',
            boxShadow: '0 16px 48px rgba(37, 99, 235, 0.12)',
          }}
        >
          <p
            style={{
              fontSize: '0.88rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#64748B',
              marginBottom: '22px',
            }}
          >
            料金プラン
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '4px', marginBottom: '8px' }}>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.6rem', fontWeight: 800, color: '#1E293B', marginBottom: '10px' }}>¥</span>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 'clamp(3rem, 9vw, 4rem)', fontWeight: 800, color: '#2563EB', lineHeight: 1 }}>1,000</span>
            <span style={{ fontSize: '1rem', color: '#64748B', marginBottom: '10px' }}>/ 月（10名まで）</span>
          </div>
          <p style={{ fontSize: '0.82rem', color: '#64748B', marginBottom: '12px' }}>
            10名を超える場合は、11名目以降 1名につき +¥100 / 月
          </p>

          {/* 人数別 月額早見 */}
          <div style={{ margin: '0 auto 28px', maxWidth: '380px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr>
                  <th style={priceThStyle}>人数</th>
                  <th style={{ ...priceThStyle, textAlign: 'right' }}>月額（税抜）</th>
                </tr>
              </thead>
              <tbody>
                {PRICE_ROWS.map((p) => (
                  <tr key={p}>
                    <td style={priceTdStyle}>{p}名</td>
                    <td style={{ ...priceTdStyle, textAlign: 'right', fontWeight: 700, color: '#1E293B' }}>
                      ¥{priceFor(p).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid rgba(0, 0, 0, 0.07)', marginBottom: '28px' }} />
          <ul style={{ listStyle: 'none', textAlign: 'left', marginBottom: '36px', padding: 0 }}>
            {[
              '出退勤打刻（ブラウザ・スマホ対応）',
              '休憩管理・実労働時間の自動計算',
              '休暇申請・承認フロー',
              'CSV / PDF出力',
              '月次集計レポート',
              'スタッフ一覧管理',
            ].map((item) => (
              <li
                key={item}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  fontSize: '0.9rem',
                  padding: '9px 0',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  color: '#1E293B',
                }}
              >
                <span style={{ fontWeight: 700, color: '#16A34A', flexShrink: 0 }}>✓</span>
                {item}
              </li>
            ))}
          </ul>
          <a
            href={appHref('/company-register')}
            onClick={onAppLinkClick('/company-register')}
            style={{
              display: 'block',
              textAlign: 'center',
              padding: '16px 28px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 700,
              transition: 'all 0.2s',
              background: '#2563EB',
              color: '#fff',
              boxShadow: '0 8px 22px rgba(37, 99, 235, 0.33)',
              border: 'none',
              cursor: 'pointer',
              width: '100%',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1D4ED8';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#2563EB';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {CTA_LABEL}
          </a>
        </div>
        <p style={{ marginTop: '22px', fontSize: '0.78rem', color: '#64748B', textAlign: 'center' }}>
          ※ 料金は税抜表示です。初期費用・解約手数料は一切かかりません。
        </p>
      </section>

      {/* FAQ */}
      <section id="faq" className="sec-pad" style={{ background: '#F1F5F9', paddingTop: '92px', paddingBottom: '92px' }}>
        <div className="rv" style={{ marginBottom: '44px', textAlign: 'center' }}>
          <span style={eyebrowStyle}>FAQ</span>
          <h2 style={{ ...sectionH2Style, textAlign: 'center' }}>よくある質問</h2>
        </div>
        <div className="rv" style={{ maxWidth: '720px', margin: '0 auto' }}>
          {faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <div
                key={i}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  marginBottom: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 14px rgba(30, 41, 59, 0.04)',
                }}
              >
                <button
                  onClick={() => setOpenFaq(open ? null : i)}
                  aria-expanded={open}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '14px',
                    textAlign: 'left',
                    padding: '20px 22px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.97rem',
                    fontWeight: 700,
                    color: '#1E293B',
                    fontFamily: 'inherit',
                  }}
                >
                  <span>{f.q}</span>
                  <span style={{ color: '#2563EB', fontSize: '1.3rem', flexShrink: 0, lineHeight: 1 }}>
                    {open ? '−' : '+'}
                  </span>
                </button>
                {open && (
                  <p
                    style={{
                      padding: '0 22px 22px',
                      margin: 0,
                      fontSize: '0.9rem',
                      lineHeight: 1.85,
                      color: '#64748B',
                    }}
                  >
                    {f.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* 問合せ導線（FAQ下） */}
        <div className="rv" style={{ maxWidth: '720px', margin: '32px auto 0', textAlign: 'center' }}>
          <p style={{ fontSize: '0.9rem', color: '#64748B', marginBottom: '14px' }}>
            ほかにご不明な点がありましたら、お気軽にお問い合わせください。
          </p>
          <a
            href={CONTACT_PATH}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              color: '#2563EB',
              fontSize: '0.92rem',
              fontWeight: 700,
              padding: '12px 26px',
              borderRadius: '8px',
              textDecoration: 'none',
              border: '2px solid #2563EB',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2563EB'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#2563EB'; }}
          >
            お問い合わせフォームへ
          </a>
        </div>
      </section>

      {/* CTA */}
      <section
        id="cta"
        className="sec-pad"
        style={{
          background: '#1A2E4A',
          // 濃紺面にも細いグリッドを敷き、ヒーローと同じ幾何のトーンで通す
          backgroundImage:
            'linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          paddingTop: '80px',
          paddingBottom: '80px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-90px',
            left: '50%',
            translate: '-50% 0',
            width: '780px',
            height: '380px',
            background:
              'linear-gradient(115deg, transparent 8%, rgba(245, 158, 11, 0.16) 45%, transparent 78%)',
          }}
        />
        <h2
          style={{
            fontSize: 'clamp(1.7rem, 3.8vw, 2.8rem)',
            fontWeight: 900,
            color: '#fff',
            letterSpacing: '-0.03em',
            marginBottom: '14px',
            position: 'relative',
          }}
        >
          まずは、<em style={{ fontStyle: 'normal', color: '#F59E0B' }}>14日間無料</em>で
          <br />
          試してみてください。
        </h2>
        <p style={{ color: 'rgba(255, 255, 255, 0.58)', fontSize: '0.97rem', marginBottom: '36px', position: 'relative' }}>
          開始時の設定は不要・カード登録なし・初期費用0円・いつでも解約OK
        </p>
        <a
          href={appHref('/company-register')}
          onClick={onAppLinkClick('/company-register')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            background: '#2563EB',
            color: '#fff',
            fontSize: '1.05rem',
            fontWeight: 700,
            padding: '17px 44px',
            borderRadius: '8px',
            textDecoration: 'none',
            transition: 'all 0.2s',
            boxShadow: '0 8px 22px rgba(37, 99, 235, 0.33)',
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1D4ED8';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(37, 99, 235, 0.43)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#2563EB';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 22px rgba(37, 99, 235, 0.33)';
          }}
        >
          ✦ {CTA_LABEL}
        </a>
      </section>

      {/* FOOTER */}
      <footer
        className="sec-pad"
        style={{
          background: '#1E293B',
          color: 'rgba(255, 255, 255, 0.38)',
          paddingTop: '36px',
          paddingBottom: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '14px',
          fontSize: '0.8rem',
        }}
      >
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
        >
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              fontSize: '1.2rem',
              color: 'rgba(255, 255, 255, 0.68)',
              letterSpacing: '-0.03em',
            }}
          >
            キン<b style={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 800 }}>ログ</b>
          </span>
        </a>
        <div style={{ display: 'flex', gap: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* 記事一覧(/blog/)。ヘッダーのナビと同じく通常リンクで遷移させる */}
          <a
            href="/blog/"
            style={footerLinkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.78)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.38)')}
          >
            記事一覧
          </a>
          <a
            href={CONTACT_PATH}
            style={footerLinkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.78)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.38)')}
          >
            お問い合わせ
          </a>
          <a
            href="/terms"
            onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
            style={footerLinkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.78)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.38)')}
          >
            利用規約
          </a>
          <a
            href="/privacy"
            onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
            style={footerLinkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.78)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.38)')}
          >
            プライバシーポリシー
          </a>
          <a
            href="/legal"
            onClick={(e) => { e.preventDefault(); navigate('/legal'); }}
            style={footerLinkStyle}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.78)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.38)')}
          >
            特定商取引法に基づく表記
          </a>
        </div>
        <span>© 2025–2026 株式会社立山システム. All rights reserved.</span>
      </footer>
    </div>
  );
};

/* ---- shared inline style objects ---- */
const navLinkStyle: React.CSSProperties = {
  textDecoration: 'none',
  fontSize: '0.88rem',
  fontWeight: 500,
  color: '#64748B',
  transition: 'color 0.2s',
  display: 'block',
};

/* アプリへの導線は `<a>` で描くため、ボタンに見せる指定
   （`textDecoration` / `display` / `textAlign`）を各スタイルに持たせている。
   `<button>` の既定でしかなかったものを明示するだけで、見た目は変わらない。 */
const navOutlineBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '2px solid #2563EB',
  borderRadius: '8px',
  color: '#2563EB',
  padding: '6px 18px',
  fontSize: '0.88rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
  fontFamily: 'inherit',
  textDecoration: 'none',
  display: 'inline-block',
  textAlign: 'center',
};

const navFillBtnStyle: React.CSSProperties = {
  background: '#2563EB',
  color: '#fff',
  padding: '9px 22px',
  borderRadius: '8px',
  fontWeight: 700,
  transition: 'background 0.2s',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.88rem',
  fontFamily: 'inherit',
  textDecoration: 'none',
  display: 'inline-block',
  textAlign: 'center',
};

const heroStatNumStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '1.9rem',
  fontWeight: 800,
  color: '#2563EB',
};

const heroStatSubStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: '#64748B',
};

const eyebrowStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: '0.76rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: '#2563EB',
  marginBottom: '12px',
};

const sectionH2Style: React.CSSProperties = {
  fontSize: 'clamp(1.75rem, 3.3vw, 2.5rem)',
  fontWeight: 900,
  letterSpacing: '-0.03em',
  lineHeight: 1.25,
  marginBottom: '14px',
};

const factBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#fff',
  color: '#1D4ED8',
  fontSize: '0.82rem',
  fontWeight: 700,
  padding: '8px 16px',
  borderRadius: '8px',
  border: '1px solid rgba(37, 99, 235, 0.25)',
};

const priceThStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#64748B',
  padding: '8px 6px',
  borderBottom: '2px solid rgba(37, 99, 235, 0.2)',
};

const priceTdStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '9px 6px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  color: '#64748B',
};

const footerLinkStyle: React.CSSProperties = {
  color: 'rgba(255, 255, 255, 0.38)',
  textDecoration: 'none',
  transition: 'color 0.2s',
};

/* ---- feature cards data ---- */
const iconStroke = '#2563EB';
const FEATURES: { title: string; desc: string; icon: React.ReactNode }[] = [
  {
    title: '押した時刻が、そのまま残る',
    desc: 'ブラウザでボタンを押すだけ。時刻はサーバー側で記録するので、端末の時計を変えても勤怠は動きません。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <circle cx="13" cy="13" r="10" stroke={iconStroke} strokeWidth="2" />
        <polyline points="13,7 13,13 17,16" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: '休憩を引いた実労働が出る',
    desc: '休憩の開始・終了も1タップ。実労働時間と残業を自動で計算します。月末に電卓を叩く作業がなくなります。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <path d="M6 8 C6 8 8 6 13 6 C18 6 20 8 20 8" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <path d="M8 8 L8 17 Q8 20 13 20 Q18 20 18 17 L18 8" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18 11 C20 11 22 12 22 14 C22 16 20 17 18 17" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <line x1="6" y1="22" x2="20" y2="22" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: '有給の残りを、本人が見られる',
    desc: '付与・取得・残数を自動で計算。申請と承認もこの中で完結します。「あと何日ありますか」に答える手間が消えます。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <rect x="4" y="6" width="18" height="16" rx="3" stroke={iconStroke} strokeWidth="2" />
        <line x1="4" y1="11" x2="22" y2="11" stroke={iconStroke} strokeWidth="2" />
        <line x1="9" y1="4" x2="9" y2="8" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <line x1="17" y1="4" x2="17" y2="8" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <circle cx="9" cy="16" r="1.2" fill={iconStroke} />
        <circle cx="13" cy="16" r="1.2" fill={iconStroke} />
        <circle cx="17" cy="16" r="1.2" fill={iconStroke} />
      </svg>
    ),
  },
  {
    title: '給与計算に渡すぶんが、1回で出る',
    desc: '月次の勤怠をCSV・PDFで出力。給与計算ソフトへの取り込みや書類の提出に、転記し直す作業が要りません。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <path d="M6 4 L16 4 L22 10 L22 22 Q22 23 21 23 L5 23 Q4 23 4 22 L4 5 Q4 4 5 4 Z" stroke={iconStroke} strokeWidth="2" strokeLinejoin="round" />
        <path d="M16 4 L16 10 L22 10" stroke={iconStroke} strokeWidth="2" strokeLinejoin="round" />
        <line x1="9" y1="15" x2="17" y2="15" stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" />
        <line x1="9" y1="18.5" x2="14" y2="18.5" stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: '誰が出ているか、一画面で分かる',
    desc: '従業員ごとの打刻状況・勤務時間・休暇残数をまとめて確認できます。席を見に行かなくても状況が分かります。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <circle cx="10" cy="9" r="4" stroke={iconStroke} strokeWidth="2" />
        <path d="M3 21 C3 17 6 15 10 15 C14 15 17 17 17 21" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <circle cx="19" cy="9" r="3" stroke={iconStroke} strokeWidth="1.8" />
        <path d="M19 14.5 C21.5 14.5 24 16 24 19" stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: '締めの数字が、締めた日に出る',
    desc: '残業・深夜・休日労働まで自動で集計。数字が揃うのを待つ日がなくなります。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <rect x="3" y="14" width="4" height="8" rx="1" fill={iconStroke} opacity="0.35" />
        <rect x="9" y="10" width="4" height="12" rx="1" fill={iconStroke} opacity="0.6" />
        <rect x="15" y="6" width="4" height="16" rx="1" fill={iconStroke} />
        <polyline points="5,14 11,10 17,6" stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="3" y1="22" x2="23" y2="22" stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    // 実装: server/src/routes/users.ts の必須項目は username / password / name のみ
    title: 'メールアドレスがなくても使える',
    desc: '従業員の登録に要るのは氏名・ログインID・パスワードの3つだけ。一人ひとりのメールアドレスを用意できない職場でも止まりません。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <rect x="3" y="6" width="20" height="14" rx="2.5" stroke={iconStroke} strokeWidth="2" />
        <path d="M3.6 7.6 L13 14 L22.4 7.6" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="5" y1="21.5" x2="21" y2="4.5" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    // 実装: migration 042(論理削除)。退職者は記録を残したまま請求人数から外れる
    title: '退職しても、記録は残る',
    desc: '退職した人の打刻記録と有給の履歴はそのまま保存され、料金の対象からは外れます。記録を守るか費用を抑えるかの二択になりません。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <path d="M13 3 L22 7 V13 C22 18 18 21.5 13 23 C8 21.5 4 18 4 13 V7 Z" stroke={iconStroke} strokeWidth="2" strokeLinejoin="round" />
        <polyline points="9,13 12,16 17,10" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'アプリのように使える',
    desc: 'スマートフォンのホーム画面やパソコンのデスクトップに追加すれば、アイコンから1タップで起動。アプリストアからのインストールは不要です。',
    icon: (
      <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <rect x="7" y="2" width="12" height="22" rx="3" stroke={iconStroke} strokeWidth="2" />
        <line x1="11" y1="20.5" x2="15" y2="20.5" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <polyline points="13,7 13,13" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
        <polyline points="10.5,9.5 13,7 15.5,9.5" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default LandingPage;
